import { ApolloProvider, useReactiveVar } from '@apollo/client';
import { useLocation } from '@hulu/react-router-dom';
import { Amplify } from 'aws-amplify';
import React, { useContext, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { amplifyConfig } from '../amplify.config';
import { graphqlClient } from '../apis/graphql/graphqlAPI';
import { InnerMainContainer, MainContainer } from '../App.styled';
import AdmanToastContainer from '../components/Alert/AdmanToastContainer';
import ApplicationContentFallback from '../components/ErrorBoundary/ApplicationContentFallback';
import Header from '../components/Header';
import { StyledLoader } from '../components/styled';
import { RoutePath, selectedOrganizationIdVar, userAmsUserIdVar } from '../constants';
import { isDemoEnvironment } from '../constants/global';
import { GhostModeContext } from '../context/GhostMode/GhostMode';
import { GlobalPermissionsContainer } from '../context/GlobalPermissions/GlobalPermissions';
import useDevEnvironmentData from '../DevEnvironment/useDevEnvironmentData';
import useSetUserFromCurrentSession from '../hooks/useSetUserFromCurrentSession';
import logErrorBoundary from '../utils/logErrorBoundary';
import { rerouteToHubIfNeededWithAuth } from '../utils/rerouteToHubIfNeeded';
import MainRoutes from './MainRoutes';
import MarketerRoutes from './MarketerRoutes';
import { StyledAppWrapper, StyledLoaderWrapper } from './styled';

Amplify.configure(amplifyConfig);

const mockOrgId = 'organization-id-1';

export const MarketerApp = (): JSX.Element => {
  const { pathname } = useLocation();
  const { isGhostMode } = useContext(GhostModeContext);
  const selectedUserId = useReactiveVar(userAmsUserIdVar);
  let selectedOrganizationId = useReactiveVar(selectedOrganizationIdVar);
  const { userInfo, loading } = useSetUserFromCurrentSession();

  if (isDemoEnvironment) {
    selectedOrganizationId = mockOrgId;
  }

  const { setInitialData } = useDevEnvironmentData();

  useEffect(() => {
    if (!pathname.includes(RoutePath.LOGOUT)) {
      rerouteToHubIfNeededWithAuth();
    }
  }, []);

  useEffect(() => {
    if (isDemoEnvironment) {
      setInitialData();
      selectedOrganizationIdVar(mockOrgId);
    }
  }, [setInitialData]);

  useEffect(() => {
    if (!selectedUserId && userInfo?.ams_user_id && !pathname.includes('logout')) {
      userAmsUserIdVar(userInfo?.ams_user_id);
    }
  }, [pathname, selectedUserId, userInfo?.ams_user_id]);

  if (loading || (isGhostMode && !selectedOrganizationId)) {
    return (
      <StyledLoaderWrapper>
        <StyledLoader data-testid="loader" />
      </StyledLoaderWrapper>
    );
  }

  const userId = userInfo?.ams_user_id || selectedUserId;
  const isLoggedUser = !!(userId && selectedOrganizationId);

  return (
    <ApolloProvider client={graphqlClient}>
      <GlobalPermissionsContainer>
        <ErrorBoundary FallbackComponent={ApplicationContentFallback} onError={logErrorBoundary}>
          <StyledAppWrapper data-testid="marketerApp">
            <Header isNotLoggedInMarketerUser={!isLoggedUser} />

            <MainContainer>
              <InnerMainContainer>{isLoggedUser ? <MarketerRoutes /> : <MainRoutes />}</InnerMainContainer>
            </MainContainer>

            <AdmanToastContainer />
          </StyledAppWrapper>
        </ErrorBoundary>
      </GlobalPermissionsContainer>
    </ApolloProvider>
  );
};

export default MarketerApp;
